import { Box, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { RcraInfoFederalWasteGeneratorStatus, UnitType } from "generated-graphql/graphql";

import { ClockProjection, projectCentralAccumulationClock } from "./containerClockPreview";
import { formatQuantity, UNIT_LABELS } from "./containerDisplay";
import { SectionCard } from "./containerSections";

/** Ring geometry from the specs' clock card. */
const DIAL_SIZE = 148;

/**
 * The projected accumulation clock as a ring: how much of the window will be
 * gone by today, and how much is left in the middle.
 */
function ClockPreviewDial({ projection }: { projection: ClockProjection }) {
  const theme = useTheme();
  const used = Math.min(projection.totalDays, projection.totalDays - projection.daysRemaining);
  const fraction = Math.max(0, Math.min(1, used / projection.totalDays));
  const overdue = projection.daysRemaining < 0;
  // Warning, not success, for a clock that hasn't started running down: this
  // is a projection of a deadline, and the reader is being shown the shape of
  // a commitment rather than a state that is currently fine.
  const ringColor = overdue ? theme.palette.error.main : theme.palette.warning.main;

  return (
    <Stack spacing={2} alignItems="center">
      <Box sx={{ position: "relative", width: DIAL_SIZE, height: DIAL_SIZE }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={DIAL_SIZE}
          thickness={4}
          sx={{ color: "grey.300", position: "absolute", inset: 0 }}
        />
        <CircularProgress
          variant="determinate"
          value={fraction * 100}
          size={DIAL_SIZE}
          thickness={4}
          sx={{ color: ringColor, position: "absolute", inset: 0 }}
        />
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ position: "absolute", inset: 0, textAlign: "center" }}
        >
          <Typography variant="h5" sx={{ lineHeight: 1 }}>
            {Math.abs(projection.daysRemaining)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {overdue ? "days overdue" : "days left"}
          </Typography>
        </Stack>
      </Box>
      <Stack spacing={1} sx={{ width: "100%" }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Deadline
          </Typography>
          <Typography variant="body2">{projection.deadline.toFormat("LLLL d, yyyy")}</Typography>
        </Box>
        <StatusChip label={projection.basis} color="default" variant="soft" />
      </Stack>
    </Stack>
  );
}

/** The fill gauge, drawn from what the form has been given so far. */
function FillPreview({
  amount,
  amountUnit,
  capacity,
  capacityUnit,
}: {
  amount: number | null;
  amountUnit: UnitType | null;
  capacity: number | null;
  capacityUnit: UnitType | null;
}) {
  // Only meaningful when both amounts are known in the same, stated unit — a
  // gallons amount against a pounds capacity is not a fraction of anything,
  // and two unset units are not a match just because they're both null.
  const comparable =
    amount != null &&
    capacity != null &&
    capacity > 0 &&
    amountUnit != null &&
    capacityUnit != null &&
    amountUnit === capacityUnit;
  const fraction = comparable ? amount / capacity : null;

  return (
    <Stack direction="row" spacing={3} alignItems="center">
      <Box
        sx={{
          position: "relative",
          width: 64,
          height: 120,
          borderRadius: 2,
          bgcolor: "grey.300",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {fraction !== null && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: `${Math.min(100, fraction * 100)}%`,
              bgcolor: "rgba(15, 95, 107, 0.5)",
              borderTop: fraction > 0 ? "1px solid #fff" : "none",
              "@media (prefers-reduced-motion: no-preference)": {
                transition: "height 0.3s ease-in-out",
              },
            }}
          />
        )}
      </Box>
      <Stack spacing={1}>
        {/* The x-of-y reading needs a stated unit behind both numbers;
            without one, say only what the form actually knows rather than
            naming a unit the reader never picked. */}
        <Typography variant="body2">
          {comparable && capacityUnit
            ? `${amount}/${capacity} ${UNIT_LABELS[capacityUnit]}`
            : formatQuantity(amount, amountUnit)}
        </Typography>
        {/* A container being created has no fill history yet; saying so beats
            a "0 adds" tally that reads like something went wrong. */}
        <Typography variant="caption" color="text.secondary">
          {amount != null ? "Recorded as the first fill event" : "No amount recorded yet"}
        </Typography>
      </Stack>
    </Stack>
  );
}

/**
 * The live rail beside the add-container form: what the container's clock and
 * fill will look like once it is saved.
 *
 * Everything here is derived from the form's current values, so it answers
 * "what am I committing to?" while the reader still has the fields in front of
 * them. Each card renders only once it has enough to say something true —
 * before that the rail is quiet rather than showing zeros.
 */
export function ContainerFormPreview({
  generatorStatus,
  movedToCentralDate,
  sqgOver200Miles,
  amount,
  amountUnit,
  capacity,
  capacityUnit,
}: {
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
  movedToCentralDate: string | null;
  sqgOver200Miles: boolean;
  amount: number | null;
  amountUnit: UnitType | null;
  capacity: number | null;
  capacityUnit: UnitType | null;
}) {
  const projection = projectCentralAccumulationClock({
    generatorStatus,
    movedToCentralDate,
    sqgOver200Miles,
  });

  // Both cards render from the start, empty until the fields behind them are
  // filled: the rail is there to show what these answers commit the container
  // to, and a rail that appears halfway through the form draws the eye away
  // from the field being typed into.
  return (
    <Stack spacing={2}>
      <SectionCard title="Central accumulation clock">
        {projection ? (
          <ClockPreviewDial projection={projection} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {generatorStatus
              ? "Set the moved-to-central date to project this container's deadline."
              : "This facility's generator status isn't known, so no storage deadline can be projected."}
          </Typography>
        )}
      </SectionCard>
      <SectionCard title="Fill">
        <FillPreview
          amount={amount}
          amountUnit={amountUnit}
          capacity={capacity}
          capacityUnit={capacityUnit}
        />
      </SectionCard>
    </Stack>
  );
}
